<template>
  <div class="z-index-2">
    <div class="p-3 pb-0">
      <h6>{{ title }}</h6>
    </div>
    <div v-if="alert" class="pt-0">
      <alert
        class="mt-0 mb-5 ms-3 me-4"
        title="Warning"
        body="The resting heart rate (RHR) has shown an upward trend over the past week, indicating a potential decline in overall wellness. Keep an eye out for signs of decreasing well-being."
        :show-icon="true"
        color="warning"
        bg-color="info"
      />
    </div>
    <div class="p-3 pt-0">
      <div class="chart">
        <canvas :id="id" class="chart-canvas" :height="height"></canvas>
      </div>
    </div>
  </div>
</template>

<script>
import ChartUtils from "@/helpers/chartUtils";
import Chart from "chart.js/auto";
import annotationPlugin from "chartjs-plugin-annotation";
import styles from "./UserRecoveryChart.vue?vue&type=style&index=0&lang=scss&module=1";
import Alert from "@/shared/Cards/Alert.vue";

Chart.register(annotationPlugin);

export default {
  name: "UserRhrChart",
  components: {
    Alert,
  },
  props: {
    id: {
      type: String,
      default: "user-rhr-chart",
    },
    title: {
      type: String,
      default: "",
    },
    height: {
      type: String,
      default: "300",
    },
    chart: {
      type: Object,
      required: true,
      labels: Array,
      datasets: {
        type: Array,
        label: String,
        data: Array,
      },
    },
  },
  data() {
    return {
      alert: null,
    };
  },
  watch: {
    chart() {
      this.addChart();
    },
  },
  mounted() {
    Chart.defaults.borderColor = styles.darkGray;

    this.addChart();
  },
  methods: {
    addChart() {
      const ctx = document.getElementById(this.id).getContext("2d");

      let gradientStroke1 = ctx.createLinearGradient(0, 300, 0, 50);
      gradientStroke1.addColorStop(1, "rgba(59,121,130,0.15)");
      gradientStroke1.addColorStop(0.5, "transparent");
      gradientStroke1.addColorStop(0, "transparent");

      let chartStatus = Chart.getChart(this.id);
      if (chartStatus != undefined) {
        chartStatus.destroy();
      }

      new Chart(ctx, {
        data: {
          labels: this.chart.labels,
          datasets: [
            {
              type: "line",
              label: this.chart.datasets[0].label,
              tension: 0.4,
              borderWidth: 0,
              pointRadius: 3,
              pointBackgroundColor: styles.colorRecovery,
              borderColor: styles.colorRecovery,
              // eslint-disable-next-line no-dupe-keys
              borderWidth: 3,
              backgroundColor: gradientStroke1,
              data: this.chart.datasets[0].data,
              fill: true,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            annotation: {
              annotations: {
                line1: {
                  type: "line",
                  borderColor: styles.colorLimitline,
                  borderDash: [6, 6],
                  borderDashOffset: 0,
                  borderWidth: 1,
                  label: {
                    display: true,
                    content: (ctx) =>
                      `Avg: ${Math.round(ChartUtils.average(ctx, 0))} bpm`,
                    position: "start",
                  },
                  scaleID: "y",
                  value: (ctx) => ChartUtils.average(ctx, 0),
                },
              },
            },
            legend: {
              position: "bottom",
              labels: {
                color: styles.colorAxes,
                boxWidth: 12,
                boxHeight: 12,
                padding: 40,
              },
            },
          },
          interaction: {
            intersect: false,
            mode: "index",
          },
          scales: {
            x: {
              grid: {
                drawBorder: false,
                display: true,
                drawOnChartArea: true,
                drawTicks: true,
                borderDash: [5, 5],
              },
              ticks: {
                display: true,
                color: styles.gray,
                padding: 10,
                font: {
                  size: 12,
                  style: "normal",
                  lineHeight: 2,
                },
              },
            },
            y: {
              display: true,
              position: "left",
              min: 0,
              ticks: {
                stepSize: 10,
                color: styles.gray,
              },
              title: {
                display: true,
                text: this.chart.datasets[0].label,
                color: styles.colorAxes,
                font: {
                  family: "NotoSans-Regular",
                },
              },
              // grid line settings
              grid: {
                drawOnChartArea: true,
              },
            },
          },
        },
      });
    },
  },
};
</script>
<style lang="scss" scoped>
:export {
  darkSecondary: $g-secondary_600;
  secondary: $g-secondary_500;
  lightSecondary: $g-secondary_200;
  gray: $color-muted-text;
  darkGray: $color-gridlines;
  colorRecovery: $color-recovery;
  colorAxes: $color-chart-axes-text;
  colorLimitline: $color-chart-limitline;
  red: $g-red_500;
  green: $g-cableGreen_500;
  blue: $g-blue_500;
}
</style>
